/**
 * Live billing dashboard that fetches real data from the API.
 */
import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PauseCircleIcon from '@mui/icons-material/PauseCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js'
import { Stripe, StripeEmbeddedCheckoutOptions } from '@stripe/stripe-js'
import { UUID } from 'crypto'
import {
  fetchBillingDashboard,
  changeTier,
  cancelSubscription,
  reactivateSubscription,
  BillingDashboardResponse,
  ActiveDiscount,
  ChangeBillingResponse,
  BillingInfo,
  ChangeBillingError,
  cancelPendingBillingTransition,
} from '@/api/billingAPI'
import {
  applyDiscountCode,
  removeDiscountCode,
  DiscountCodeValidation,
} from '@/api/subscriptionsAPI'
import DiscountCodeInput from '@/components/DiscountCodeInput'
import {
  getTierLabel,
  getBillingAmount,
  TIER_PRICING as BASE_TIER_PRICING,
} from '@/utils/billingConstants'
import MembershipUpgrade from '@/components/MembershipUpgrade'
import ChangeBillingForm from '@/components/billing/ChangeBillingForm'

// Tier pricing for display
const TIER_PRICING: Record<
  number,
  { monthly: number; quarterly: number; annual: number; turnover: string }
> = {
  0: {
    monthly: BASE_TIER_PRICING[0].monthly,
    quarterly: getBillingAmount(BASE_TIER_PRICING[0].monthly, 'quarterly'),
    annual: getBillingAmount(BASE_TIER_PRICING[0].monthly, 'annual'),
    turnover: '<$40k',
  },
  1: {
    monthly: BASE_TIER_PRICING[1].monthly,
    quarterly: getBillingAmount(BASE_TIER_PRICING[1].monthly, 'quarterly'),
    annual: getBillingAmount(BASE_TIER_PRICING[1].monthly, 'annual'),
    turnover: 'Over $33m',
  },
  2: {
    monthly: BASE_TIER_PRICING[2].monthly,
    quarterly: getBillingAmount(BASE_TIER_PRICING[2].monthly, 'quarterly'),
    annual: getBillingAmount(BASE_TIER_PRICING[2].monthly, 'annual'),
    turnover: '$5m–$33m',
  },
  3: {
    monthly: BASE_TIER_PRICING[3].monthly,
    quarterly: getBillingAmount(BASE_TIER_PRICING[3].monthly, 'quarterly'),
    annual: getBillingAmount(BASE_TIER_PRICING[3].monthly, 'annual'),
    turnover: '$140k–$5m',
  },
  4: {
    monthly: BASE_TIER_PRICING[4].monthly,
    quarterly: getBillingAmount(BASE_TIER_PRICING[4].monthly, 'quarterly'),
    annual: getBillingAmount(BASE_TIER_PRICING[4].monthly, 'annual'),
    turnover: '<$140k',
  },
}

interface BillingDashboardLiveProps {
  orgUUID: string
  token: string
  onManageSubscription?: () => void
  pauseResumeEnabled?: boolean
  children?: React.ReactNode // For SubscriptionManagement component
  stripePromise?: Promise<Stripe | null> // For reactivating terminated subscriptions
  onRefresh?: () => void // Called after billing changes to refresh related components
  onSyncStatusChange?: (status: string | null | undefined) => void // Called when sync status changes
  orgName?: string
  userEmail?: string
  userFirstName?: string
  userLastName?: string
  billingEmail: string | null | undefined
  onUpdateBillingInfo: (
    updates: Partial<BillingInfo>,
  ) => Promise<BillingInfo | null>
}

const getTierColor = (tier: number) => {
  const colors: Record<number, string> = {
    0: '#607D8B',
    1: '#4CAF50',
    2: '#2196F3',
    3: '#9C27B0',
    4: '#FF9800',
  }
  return colors[tier] || '#757575'
}

const getStatusIcon = (status: string, hasPendingCancellation?: boolean) => {
  if (hasPendingCancellation) {
    return <CancelIcon sx={{ color: '#FF9800', mr: 1 }} />
  }
  switch (status?.toLowerCase()) {
    case 'active':
      return <CheckCircleIcon sx={{ color: '#4CAF50', mr: 1 }} />
    case 'paused':
      return <PauseCircleIcon sx={{ color: '#FF9800', mr: 1 }} />
    case 'cancelled':
      return <CancelIcon sx={{ color: '#f44336', mr: 1 }} />
    default:
      return null
  }
}

const getStatusLabel = (status: string, cancellationDate?: string | null) => {
  // If status is active but has a cancellation date, it's pending cancellation
  if (status?.toLowerCase() === 'active' && cancellationDate) {
    return `Cancels on ${formatDate(cancellationDate)}`
  }
  switch (status?.toLowerCase()) {
    case 'active':
      return 'Active'
    case 'paused':
      return 'Paused'
    case 'cancelled':
      return 'Cancelled'
    default:
      return status || 'Unknown'
  }
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-'
  // Date-only ISO strings ("2026-06-15") parse as UTC midnight, which then
  // shifts into the browser's local timezone — appearing as the next day in
  // NZ and the previous day in US offsets. Parse YYYY-MM-DD as a local date
  // so renewals/cancellations show on the date the user expects.
  const datePart = dateStr.split('T')[0]
  const ymd = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  const date = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(dateStr)
  return date.toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

const formatScheduledBillingDate = (dateStr: string) => {
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return '-'
  return new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

const formatCurrency = (amount: number | null) => {
  if (amount === null) return null
  return `$${amount.toFixed(2)}`
}

const isCanonicalUUID = (value: string | null | undefined) =>
  !!value &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  )

const getBillingPeriodLabel = (frequency: string) => {
  switch (frequency?.toLowerCase()) {
    case 'monthly':
      return '/month'
    case 'quarterly':
      return '/quarter'
    case 'yearly':
    case 'annual':
      return '/year'
    default:
      return ''
  }
}

export default function BillingDashboardLive({
  orgUUID,
  token,
  onManageSubscription,
  pauseResumeEnabled,
  children,
  stripePromise,
  onRefresh,
  onSyncStatusChange,
  orgName,
  userEmail,
  userFirstName,
  userLastName,
  billingEmail,
  onUpdateBillingInfo,
}: BillingDashboardLiveProps) {
  const [data, setData] = useState<BillingDashboardResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Tier change state
  const [showTierDialog, setShowTierDialog] = useState(false)
  const [tierLoading, setTierLoading] = useState(false)
  const [tierError, setTierError] = useState<string | null>(null)
  const [tierSuccess, setTierSuccess] = useState<string | null>(null)
  const [pendingTierChange, setPendingTierChange] = useState<number | null>(
    null,
  )

  // Unified change-billing state
  const [showChangeBilling, setShowChangeBilling] = useState(false)
  const [changeBillingSuccess, setChangeBillingSuccess] = useState<
    string | null
  >(null)
  const [showCancelScheduledDialog, setShowCancelScheduledDialog] =
    useState(false)
  const [cancelScheduledTransitionId, setCancelScheduledTransitionId] =
    useState<string | null>(null)
  const [cancelScheduledLoading, setCancelScheduledLoading] = useState(false)
  const [cancelScheduledError, setCancelScheduledError] = useState<
    string | null
  >(null)
  const [cancelScheduledNotice, setCancelScheduledNotice] = useState<{
    severity: 'success' | 'info' | 'warning'
    text: string
  } | null>(null)
  const cancelScheduledSubmittingRef = useRef(false)

  // Cancel subscription state
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancelStep, setCancelStep] = useState<'pitch' | 'feedback'>('pitch')
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [cancelSuccess, setCancelSuccess] = useState<string | null>(null)
  const [cancelReasonCategory, setCancelReasonCategory] = useState('')
  const [cancelReason, setCancelReason] = useState('')

  // Reactivate subscription state
  const [showReactivateDialog, setShowReactivateDialog] = useState(false)
  const [showMembershipUpgrade, setShowMembershipUpgrade] = useState(false)
  const [reactivateLoading, setReactivateLoading] = useState(false)
  const [reactivateError, setReactivateError] = useState<string | null>(null)
  const [reactivateSuccess, setReactivateSuccess] = useState<string | null>(
    null,
  )
  const [checkoutOptions, setCheckoutOptions] =
    useState<StripeEmbeddedCheckoutOptions | null>(null)
  const [showCheckout, setShowCheckout] = useState(false)

  // Apply discount state
  const [discountValidation, setDiscountValidation] =
    useState<DiscountCodeValidation | null>(null)
  const [applyDiscountLoading, setApplyDiscountLoading] = useState(false)
  const [applyDiscountError, setApplyDiscountError] = useState<string | null>(
    null,
  )
  const [applyDiscountSuccess, setApplyDiscountSuccess] = useState<
    string | null
  >(null)

  // Remove discount state
  const [showRemoveDiscountDialog, setShowRemoveDiscountDialog] =
    useState(false)
  const [removeDiscountLoading, setRemoveDiscountLoading] = useState(false)
  const [removeDiscountError, setRemoveDiscountError] = useState<string | null>(
    null,
  )

  useEffect(() => {
    async function loadData() {
      if (!orgUUID || !token) return

      setLoading(true)
      setError(null)

      try {
        const dashboardData = await fetchBillingDashboard(orgUUID, token)
        setData(dashboardData)
        // Report sync status to parent
        onSyncStatusChange?.(dashboardData.billing_sync_status)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load billing data',
        )
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [orgUUID, token, onSyncStatusChange])

  const refetchData = async (): Promise<BillingDashboardResponse | null> => {
    try {
      const dashboardData = await fetchBillingDashboard(orgUUID, token)
      setData(dashboardData)
      // Report sync status to parent
      onSyncStatusChange?.(dashboardData.billing_sync_status)
      return dashboardData
    } catch (err) {
      console.error('Failed to refetch billing data')
      return null
    }
  }

  const handleApplyDiscount = async () => {
    if (!discountValidation?.valid || !discountValidation.code) {
      setApplyDiscountError('Enter a valid discount code before applying.')
      return
    }
    setApplyDiscountLoading(true)
    setApplyDiscountError(null)
    setApplyDiscountSuccess(null)
    try {
      const response = await applyDiscountCode(
        orgUUID,
        discountValidation.code,
        token,
      )
      setApplyDiscountSuccess(
        response.message || 'Discount applied to your subscription.',
      )
      setDiscountValidation(null)
      await refetchData()
      onRefresh?.()
      setTimeout(() => setApplyDiscountSuccess(null), 5000)
    } catch (err) {
      setApplyDiscountError(
        err instanceof Error ? err.message : 'Failed to apply discount code',
      )
    } finally {
      setApplyDiscountLoading(false)
    }
  }

  const handleRemoveDiscount = async () => {
    setRemoveDiscountLoading(true)
    setRemoveDiscountError(null)
    try {
      await removeDiscountCode(orgUUID, token)
      setShowRemoveDiscountDialog(false)
      await refetchData()
      onRefresh?.()
    } catch (err) {
      setRemoveDiscountError(
        err instanceof Error ? err.message : 'Failed to remove discount',
      )
    } finally {
      setRemoveDiscountLoading(false)
    }
  }

  const handleTierChange = async (newTier: number) => {
    setTierLoading(true)
    setTierError(null)
    setTierSuccess(null)
    try {
      const response = await changeTier(orgUUID, token, newTier)
      setTierSuccess(response.message)
      setShowTierDialog(false)
      setPendingTierChange(null)
      // Refetch data to get updated tier
      await refetchData()
      // Notify parent to refresh related components (e.g., PaymentHistory)
      onRefresh?.()
      // Clear success message after 5 seconds
      setTimeout(() => setTierSuccess(null), 5000)
    } catch (err) {
      setTierError(err instanceof Error ? err.message : 'Failed to change tier')
    } finally {
      setTierLoading(false)
    }
  }

  // Called by ChangeBillingForm after a successful change-billing request.
  // Invoice -> Stripe routes return a checkout secret; everything else just
  // refreshes the dashboard and shows the success message.
  const handleChangeBillingComplete = async (
    response: ChangeBillingResponse,
  ) => {
    if (response.requires_checkout && response.checkout_client_secret) {
      setShowChangeBilling(false)
      setCheckoutOptions({ clientSecret: response.checkout_client_secret })
      setShowCheckout(true)
      return
    }
    setShowChangeBilling(false)
    setChangeBillingSuccess(response.message)
    await refetchData()
    onRefresh?.()
    setTimeout(() => setChangeBillingSuccess(null), 6000)
  }

  const closeCancelScheduledDialog = () => {
    if (cancelScheduledLoading) return
    setShowCancelScheduledDialog(false)
    setCancelScheduledTransitionId(null)
    setCancelScheduledError(null)
  }

  const handleCancelScheduledChange = async () => {
    if (
      cancelScheduledSubmittingRef.current ||
      !cancelScheduledTransitionId
    ) {
      return
    }
    cancelScheduledSubmittingRef.current = true
    setCancelScheduledLoading(true)
    setCancelScheduledError(null)
    setCancelScheduledNotice(null)
    try {
      const response = await cancelPendingBillingTransition(
        orgUUID,
        token,
        cancelScheduledTransitionId,
      )
      setShowCancelScheduledDialog(false)
      setCancelScheduledTransitionId(null)
      setCancelScheduledNotice({
        severity: 'success',
        text: response.message,
      })
      await refetchData()
      onRefresh?.()
      setTimeout(() => setCancelScheduledNotice(null), 6000)
    } catch (err) {
      if (
        err instanceof ChangeBillingError &&
        (err.status === 404 || err.status === 409)
      ) {
        setShowCancelScheduledDialog(false)
        setCancelScheduledTransitionId(null)
        const refreshed = await refetchData()
        setCancelScheduledNotice(
          refreshed === null
            ? {
                severity: 'warning',
                text: 'Could not refresh the scheduled change. Reload the page before trying again.',
              }
            : {
                severity: 'info',
                text: refreshed.pending_payment_transition
                  ? 'The scheduled change has changed. Review it before cancelling.'
                  : 'The scheduled change is no longer pending.',
              },
        )
        setTimeout(() => setCancelScheduledNotice(null), 6000)
      } else {
        setCancelScheduledError(
          err instanceof ChangeBillingError
            ? err.message
            : 'Failed to cancel the scheduled billing change.',
        )
      }
    } finally {
      cancelScheduledSubmittingRef.current = false
      setCancelScheduledLoading(false)
    }
  }

  const handleCancelDialogClose = () => {
    if (cancelLoading) return
    setShowCancelDialog(false)
    setCancelStep('pitch')
    setCancelReasonCategory('')
    setCancelReason('')
    setCancelError(null)
  }

  const handleCancelSubscription = async () => {
    setCancelLoading(true)
    setCancelError(null)
    setCancelSuccess(null)
    const reason = [cancelReasonCategory, cancelReason]
      .filter(Boolean)
      .join(': ')
    try {
      const response = await cancelSubscription(
        orgUUID,
        token,
        false, // Always cancel at period end
        reason || undefined,
      )
      setCancelSuccess(response.message)
      handleCancelDialogClose()
      // Refetch data to get updated status
      await refetchData()
      onRefresh?.()
    } catch (err) {
      setCancelError(
        err instanceof Error ? err.message : 'Failed to cancel subscription',
      )
    } finally {
      setCancelLoading(false)
    }
  }

  const handleReactivateSubscription = async () => {
    setReactivateLoading(true)
    setReactivateError(null)
    setReactivateSuccess(null)
    try {
      const response = await reactivateSubscription(orgUUID, token)

      // Check if subscription is fully terminated and needs new checkout
      if (
        response.requires_new_subscription &&
        response.checkout_client_secret
      ) {
        setShowReactivateDialog(false)
        setCheckoutOptions({
          clientSecret: response.checkout_client_secret,
        })
        setShowCheckout(true)
        return
      }

      setReactivateSuccess(response.message)
      setShowReactivateDialog(false)
      // Refetch data to get updated status
      await refetchData()
    } catch (err) {
      setReactivateError(
        err instanceof Error
          ? err.message
          : 'Failed to reactivate subscription',
      )
    } finally {
      setReactivateLoading(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    )
  }

  if (!data) {
    return null
  }

  if (showMembershipUpgrade && stripePromise) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Button
          onClick={() => setShowMembershipUpgrade(false)}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          &larr; Back to billing
        </Button>
        <MembershipUpgrade
          orgUUID={orgUUID as UUID}
          orgName={orgName || ''}
          token={token}
          stripePromise={stripePromise}
          tier={data.subscription.tier ?? undefined}
          onError={(err) => setError(err)}
          onSuccess={async () => {
            setShowMembershipUpgrade(false)
            onRefresh?.()
            await refetchData()
          }}
          userEmail={userEmail}
          userFirstName={userFirstName}
          userLastName={userLastName}
        />
      </Box>
    )
  }

  const { subscription, billing_info, discounts, recent_payments } = data
  const tierInfo =
    subscription.tier !== null && subscription.tier !== undefined
      ? TIER_PRICING[subscription.tier]
      : null
  const isInvoiceSubscriber =
    subscription.payment_type?.toLowerCase() === 'invoice'
  const discountFrequency = subscription.billing_frequency?.toLowerCase()
  const discountFrequencyMultiplier: 1 | 3 | 12 =
    discountFrequency === 'monthly'
      ? 1
      : discountFrequency === 'quarterly'
        ? 3
        : 12
  // `billing_amount` is only persisted for custom prices (incl. $0 from a
  // 100% discount); otherwise mirror the server's _calculate_original_amount
  // fallback to tier × frequency. Use nullish coalescing so a stored $0
  // doesn't get clobbered back to the tier price.
  const tierDerivedAmount = tierInfo
    ? discountFrequencyMultiplier === 1
      ? tierInfo.monthly
      : discountFrequencyMultiplier === 3
        ? tierInfo.quarterly
        : tierInfo.annual
    : 0
  const effectivePeriodAmount = subscription.billing_amount ?? tierDerivedAmount
  const formattedAmount = formatCurrency(effectivePeriodAmount)
  const canApplyDiscount =
    isInvoiceSubscriber && discounts.length === 0 && tierDerivedAmount > 0
  const pendingInvoiceTransition =
    subscription.payment_type?.toLowerCase() !== 'invoice'
      ? data.pending_payment_transition
      : null
  const hasPendingInvoiceTransition =
    subscription.payment_type?.toLowerCase() !== 'invoice' &&
    (data.has_pending_payment_transition ?? !!pendingInvoiceTransition)
  const pendingTransitionId = pendingInvoiceTransition?.transition_id
  const canCancelPendingTransition = isCanonicalUUID(pendingTransitionId)
  // Check if subscription is pending cancellation (active but with cancellation date set)
  const isPendingCancellation =
    subscription.subscription_status?.toLowerCase() === 'active' &&
    !!subscription.cancellation_date &&
    !hasPendingInvoiceTransition

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {pendingInvoiceTransition && (
        <Alert
          severity="info"
          sx={{ mb: 3, alignItems: { xs: 'flex-start', sm: 'center' } }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1, sm: 2 },
              width: '100%',
            }}
          >
            <Box>
              <strong>Payment method change scheduled.</strong> Invoice billing
              effective{' '}
              <strong>
                {formatScheduledBillingDate(
                  pendingInvoiceTransition.effective_at,
                )}
              </strong>
              .
              {!canCancelPendingTransition && (
                <Typography component="div" variant="body2" sx={{ mt: 0.5 }}>
                  Contact support if you need to cancel this scheduled change.
                </Typography>
              )}
            </Box>
            {canCancelPendingTransition && (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setCancelScheduledTransitionId(pendingTransitionId as string)
                  setCancelScheduledError(null)
                  setShowCancelScheduledDialog(true)
                }}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
              >
                Cancel scheduled change
              </Button>
            )}
          </Box>
        </Alert>
      )}

      {hasPendingInvoiceTransition && !pendingInvoiceTransition && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Payment method change scheduled.</strong> Its details cannot
          be displayed safely. Contact support to review or cancel this change.
        </Alert>
      )}

      {/* Pending Cancellation Alert */}
      {isPendingCancellation && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Your subscription is set to cancel on{' '}
          <strong>{formatDate(subscription.cancellation_date)}</strong>.
          You&apos;ll keep access until then. Click &quot;Reactivate&quot; to
          continue your subscription.
        </Alert>
      )}

      {/* Success messages */}
      {tierSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {tierSuccess}
        </Alert>
      )}
      {changeBillingSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {changeBillingSuccess}
        </Alert>
      )}
      {cancelScheduledNotice && (
        <Alert severity={cancelScheduledNotice.severity} sx={{ mb: 3 }}>
          {cancelScheduledNotice.text}
        </Alert>
      )}
      {cancelSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {cancelSuccess}
        </Alert>
      )}
      {reactivateSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {reactivateSuccess}
        </Alert>
      )}

      {/* Subscription Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Subscription Overview
          </Typography>

          <Grid container spacing={4}>
            {/* Left Column - Subscription Details */}
            <Grid
              item
              xs={12}
              md={discounts.length > 0 || canApplyDiscount ? 7 : 12}
            >
              {/* Tier Badge with Turnover */}
              {subscription.tier !== null &&
                subscription.tier !== undefined &&
                tierInfo && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Chip
                      label={getTierLabel(subscription.tier)}
                      sx={{
                        backgroundColor: getTierColor(subscription.tier),
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {tierInfo.turnover} turnover
                    </Typography>
                  </Box>
                )}

              {/* Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                {getStatusIcon(
                  subscription.subscription_status,
                  !!subscription.cancellation_date,
                )}
                <Typography variant="body1">
                  Status:{' '}
                  <strong>
                    {getStatusLabel(
                      subscription.subscription_status,
                      subscription.cancellation_date,
                    )}
                  </strong>
                </Typography>
              </Box>

              {/* Billing Info */}
              <Typography variant="body1" sx={{ mb: 1 }}>
                Billing: <strong>{subscription.billing_frequency}</strong> via{' '}
                <strong>{subscription.payment_type}</strong>
              </Typography>

              {pendingInvoiceTransition && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Scheduled:{' '}
                  <strong>
                    {pendingInvoiceTransition.target_billing_frequency} via
                    Invoice
                  </strong>
                  , effective{' '}
                  <strong>
                    {formatScheduledBillingDate(
                      pendingInvoiceTransition.effective_at,
                    )}
                  </strong>
                </Typography>
              )}

              {/* Billing Email */}
              {billing_info.billing_email && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Typography variant="body1">
                    Billing Email: <strong>{billing_info.billing_email}</strong>
                  </Typography>
                  <Link
                    href="#"
                    underline="none"
                    sx={{ color: '#2196F3', fontSize: '0.875rem' }}
                    onClick={(e) => e.preventDefault()}
                  >
                    Edit
                  </Link>
                </Box>
              )}

              {/* Large Price Display */}
              {formattedAmount && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '2.5rem',
                      fontWeight: 300,
                      color: '#2196F3',
                    }}
                  >
                    {formattedAmount}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '1rem',
                      color: 'text.secondary',
                      ml: 0.5,
                    }}
                  >
                    {getBillingPeriodLabel(subscription.billing_frequency)}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                      ml: 1,
                    }}
                  >
                    +GST
                  </Typography>
                </Box>
              )}

              {/* Next Renewal / Access Until */}
              {(hasPendingInvoiceTransition ||
                subscription.next_billing_date) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {pendingInvoiceTransition
                    ? `Invoice billing effective: ${formatScheduledBillingDate(
                        pendingInvoiceTransition.effective_at,
                      )}`
                    : hasPendingInvoiceTransition
                      ? 'Payment method change scheduled'
                    : isPendingCancellation ||
                  subscription.subscription_status?.toLowerCase() ===
                    'cancelled'
                      ? `Access until: ${formatDate(subscription.next_billing_date)}`
                      : `Next renewal: ${formatDate(subscription.next_billing_date)}`}
                </Typography>
              )}

              {/* Action Buttons */}
              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {/* Change Tier Button */}
                <Button
                  variant="outlined"
                  onClick={() => setShowTierDialog(true)}
                  sx={{
                    borderRadius: 3,
                    textTransform: 'none',
                    px: 3,
                  }}
                >
                  Change Tier
                </Button>

                {/* Change Payment Method button (active subscriptions only, not pending cancellation) */}
                {subscription.subscription_status?.toLowerCase() === 'active' &&
                  !isPendingCancellation && (
                    <Tooltip
                      title={
                        hasPendingInvoiceTransition
                          ? 'Cancel the scheduled change before choosing another payment method.'
                          : ''
                      }
                      describeChild
                    >
                      <Box component="span">
                        <Button
                          variant="outlined"
                          onClick={() => setShowChangeBilling(true)}
                          disabled={hasPendingInvoiceTransition}
                          sx={{
                            borderRadius: 3,
                            textTransform: 'none',
                            px: 3,
                          }}
                        >
                          Change Payment Method
                        </Button>
                      </Box>
                    </Tooltip>
                  )}

                {/* Manage Subscription Button (Stripe only) */}
                {subscription.payment_type?.toLowerCase() === 'stripe' &&
                  onManageSubscription && (
                    <Button
                      variant="contained"
                      onClick={onManageSubscription}
                      sx={{
                        borderRadius: 3,
                        bgcolor: '#1C3045',
                        textTransform: 'none',
                        px: 4,
                      }}
                    >
                      Manage Subscription
                    </Button>
                  )}

                {/* Cancel Subscription Button (for active or paused subscriptions, not pending cancellation) */}
                {['active', 'paused'].includes(
                  subscription.subscription_status?.toLowerCase() || '',
                ) &&
                  !isPendingCancellation && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setShowCancelDialog(true)}
                      sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        px: 3,
                      }}
                    >
                      Cancel Subscription
                    </Button>
                  )}

                {/* Reactivate Subscription Button (for cancelled OR pending cancellation) */}
                {(subscription.subscription_status?.toLowerCase() ===
                  'cancelled' ||
                  isPendingCancellation) && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => setShowReactivateDialog(true)}
                    sx={{
                      borderRadius: 3,
                      textTransform: 'none',
                      px: 3,
                    }}
                  >
                    Reactivate Subscription
                  </Button>
                )}
              </Box>
            </Grid>

            {/* Right Column - Active Discounts OR Apply Discount form */}
            {(discounts.length > 0 || canApplyDiscount) && (
              <Grid item xs={12} md={5}>
                {discounts.length > 0 ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600, color: '#4CAF50' }}
                    >
                      Active Discounts
                    </Typography>
                    {discounts.map((discount, index) => (
                      <Box key={index} sx={{ mb: 1.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            backgroundColor: '#E8F5E9',
                            borderRadius: 2,
                            p: 2,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600, mb: 0.5 }}
                            >
                              {discount.code}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              {discount.discount_value}% discount
                            </Typography>
                            {discount.days_remaining !== null && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Expires in {discount.days_remaining} days
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            label={`-${discount.discount_value}%`}
                            size="small"
                            sx={{
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              fontWeight: 600,
                              borderRadius: '16px',
                            }}
                          />
                        </Box>
                        {isInvoiceSubscriber && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              mt: 0.5,
                            }}
                          >
                            <Button
                              size="small"
                              color="error"
                              onClick={() => setShowRemoveDiscountDialog(true)}
                              sx={{ textTransform: 'none' }}
                            >
                              Remove discount
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </>
                ) : (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Have a discount code?
                    </Typography>

                    <DiscountCodeInput
                      originalPrice={effectivePeriodAmount}
                      frequencyMultiplier={discountFrequencyMultiplier}
                      onValidationChange={setDiscountValidation}
                    />
                    {discountValidation?.valid && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor: '#F1F8E9',
                          border: '1px solid #C5E1A5',
                          borderRadius: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Current
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            $
                            {effectivePeriodAmount.toFixed(2)}
                            {getBillingPeriodLabel(
                              subscription.billing_frequency || '',
                            )}{' '}
                            +GST
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: '#2E7D32' }}
                          >
                            Discount ({discountValidation.code})
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: '#2E7D32' }}
                          >
                            -${(discountValidation.discount_amount || 0).toFixed(
                              2,
                            )}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            pt: 1,
                            borderTop: '1px solid #C5E1A5',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            New amount
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                          >
                            $
                            {(discountValidation.final_price ?? 0).toFixed(2)}
                            {getBillingPeriodLabel(
                              subscription.billing_frequency || '',
                            )}{' '}
                            +GST
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    <Button
                      variant="contained"
                      color="success"
                      disabled={
                        !discountValidation?.valid || applyDiscountLoading
                      }
                      onClick={handleApplyDiscount}
                      sx={{
                        mt: 2,
                        borderRadius: 3,
                        textTransform: 'none',
                      }}
                    >
                      {applyDiscountLoading ? 'Applying…' : 'Apply discount'}
                    </Button>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      Discounts will apply to your next invoice — your current
                      billing period is unchanged.
                    </Typography>
                    {applyDiscountError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {applyDiscountError}
                      </Alert>
                    )}
                    {applyDiscountSuccess && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        {applyDiscountSuccess}
                      </Alert>
                    )}
                  </Box>
                )}
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Pause/Resume Management (passed as children) - hide when cancelled */}
      {pauseResumeEnabled &&
        subscription.subscription_status?.toLowerCase() !== 'cancelled' &&
        children}

      {/* Remove Discount Dialog */}
      <Dialog
        open={showRemoveDiscountDialog}
        onClose={() => {
          if (!removeDiscountLoading) {
            setShowRemoveDiscountDialog(false)
            setRemoveDiscountError(null)
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove discount?</DialogTitle>
        <DialogContent>
          {discounts[0] && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              The discount <strong>{discounts[0].code}</strong> will be removed
              from your subscription and your next invoice will return to the
              standard amount.
            </Typography>
          )}
          {removeDiscountError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {removeDiscountError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowRemoveDiscountDialog(false)
              setRemoveDiscountError(null)
            }}
            disabled={removeDiscountLoading}
            sx={{ textTransform: 'none' }}
          >
            Keep discount
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRemoveDiscount}
            disabled={removeDiscountLoading}
            sx={{ textTransform: 'none' }}
          >
            {removeDiscountLoading ? 'Removing…' : 'Remove discount'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Tier Dialog */}
      <Dialog
        open={showTierDialog}
        onClose={() => {
          if (!tierLoading) {
            setShowTierDialog(false)
            setPendingTierChange(null)
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Subscription Tier</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Upgrading takes effect immediately. Downgrading takes effect at the
            end of your current billing period.
          </Alert>

          {tierError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {tierError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Sort tiers: 1-4 first, then micro (0) at end */}
            {[1, 2, 3, 4, 0].map((tierNumber) => {
              const info = TIER_PRICING[tierNumber]
              const isCurrentTier = subscription.tier === tierNumber
              const isSelected = pendingTierChange === tierNumber
              return (
                <Card
                  key={tierNumber}
                  sx={{
                    p: 2,
                    cursor:
                      tierLoading || isCurrentTier ? 'not-allowed' : 'pointer',
                    border: isSelected
                      ? '2px solid #4CAF50'
                      : isCurrentTier
                        ? '2px solid #1C3045'
                        : '1px solid #ddd',
                    backgroundColor: isSelected ? '#E8F5E9' : 'inherit',
                    '&:hover': {
                      borderColor:
                        tierLoading || isCurrentTier ? undefined : '#1C3045',
                    },
                    opacity: tierLoading ? 0.7 : 1,
                  }}
                  onClick={() => {
                    if (!tierLoading && !isCurrentTier) {
                      setPendingTierChange(tierNumber)
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        label={getTierLabel(tierNumber)}
                        size="small"
                        sx={{
                          backgroundColor: getTierColor(tierNumber),
                          color: 'white',
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {info.turnover} organisational turnover
                      </Typography>
                      {isCurrentTier && (
                        <Chip label="Current" size="small" color="primary" />
                      )}
                      {isSelected && (
                        <Chip label="Selected" size="small" color="success" />
                      )}
                      {tierLoading && pendingTierChange === tierNumber && (
                        <CircularProgress size={16} />
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" component="span">
                        ${info.monthly.toFixed(2)}/mo
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        +GST
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              )
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowTierDialog(false)
              setPendingTierChange(null)
            }}
            disabled={tierLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              pendingTierChange !== null && handleTierChange(pendingTierChange)
            }
            disabled={tierLoading || pendingTierChange === null}
            sx={{
              bgcolor: '#1C3045',
              '&:hover': { bgcolor: '#2a4460' },
            }}
          >
            {tierLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Confirm Change'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unified Change Billing form */}
      <ChangeBillingForm
        open={showChangeBilling}
        onClose={() => setShowChangeBilling(false)}
        orgUUID={orgUUID}
        token={token}
        billingEmail={
          billingEmail === undefined
            ? data.billing_info.billing_email
            : billingEmail
        }
        userEmail={userEmail}
        onUpdateBillingInfo={onUpdateBillingInfo}
        currentPaymentType={subscription.payment_type}
        currentFrequency={subscription.billing_frequency}
        tier={subscription.tier}
        hasActiveDiscount={discounts.length > 0}
        onComplete={handleChangeBillingComplete}
      />

      <Dialog
        open={showCancelScheduledDialog}
        onClose={closeCancelScheduledDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel scheduled payment change?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your current Stripe billing will continue and the scheduled switch
            to invoice billing will be removed.
          </Typography>
          {cancelScheduledError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelScheduledError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeCancelScheduledDialog}
            disabled={cancelScheduledLoading}
            sx={{ textTransform: 'none' }}
          >
            Keep scheduled change
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleCancelScheduledChange}
            disabled={
              cancelScheduledLoading || !cancelScheduledTransitionId
            }
            sx={{ textTransform: 'none' }}
          >
            {cancelScheduledLoading ? 'Cancelling…' : 'Cancel scheduled change'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Subscription Dialog - Two-step retention flow */}
      <Dialog
        open={showCancelDialog}
        onClose={handleCancelDialogClose}
        maxWidth="sm"
        fullWidth
      >
        {cancelStep === 'pitch' ? (
          <>
            <DialogTitle>Before you go...</DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                We&apos;d hate to see you leave. Here are some options that
                might help:
              </Typography>

              <Box
                sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Experiencing financial hardship?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please reach out to us at{' '}
                  <Link
                    href="mailto:help@fundsorter.com"
                    target="_blank"
                    rel="noopener"
                  >
                    help@fundsorter.com
                  </Link>{' '}
                  &mdash; we may be able to help with a pause or temporary
                  discount.
                </Typography>
              </Box>

              <Box
                sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Need help making the system work for you?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Our team is here to help.{' '}
                  <Link
                    href="mailto:help@fundsorter.com"
                    target="_blank"
                    rel="noopener"
                  >
                    Get in touch
                  </Link>{' '}
                  and we&apos;ll set up a call to make sure you&apos;re getting
                  the most out of your subscription.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                onClick={handleCancelDialogClose}
                sx={{
                  bgcolor: '#1C3045',
                  '&:hover': { bgcolor: '#2a4460' },
                }}
              >
                Go Back
              </Button>
              <Button
                onClick={() => setCancelStep('feedback')}
                color="inherit"
                size="small"
              >
                I still want to cancel
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ color: '#d32f2f' }}>
              Sorry to see you go
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your subscription will be cancelled at the end of your current
                billing period. You&apos;ll keep access until then, and you can
                reactivate anytime before the period ends.
              </Typography>

              {cancelError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {cancelError}
                </Alert>
              )}

              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend">
                  We&apos;d love to know why you&apos;re leaving so we can
                  improve:
                </FormLabel>
                <RadioGroup
                  value={cancelReasonCategory}
                  onChange={(e) => setCancelReasonCategory(e.target.value)}
                >
                  <FormControlLabel
                    value="Too expensive"
                    control={<Radio />}
                    label="Too expensive"
                  />
                  <FormControlLabel
                    value="Not using it enough"
                    control={<Radio />}
                    label="Not using it enough"
                  />
                  <FormControlLabel
                    value="Missing features I need"
                    control={<Radio />}
                    label="Missing features I need"
                  />
                  <FormControlLabel
                    value="Switching to another product"
                    control={<Radio />}
                    label="Switching to another product"
                  />
                  <FormControlLabel
                    value="Just need a temporary break"
                    control={<Radio />}
                    label={
                      <Box>
                        Just need a temporary break
                        <Typography
                          variant="caption"
                          color="primary"
                          display="block"
                        >
                          Consider pausing your account instead!
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="Other"
                    control={<Radio />}
                    label="Other"
                  />
                </RadioGroup>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Any additional details? (optional)"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                size="small"
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setCancelStep('pitch')}
                disabled={cancelLoading}
              >
                Go Back
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
              >
                {cancelLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  'Cancel Subscription'
                )}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reactivate Subscription Dialog */}
      <Dialog
        open={showReactivateDialog}
        onClose={() => !reactivateLoading && setShowReactivateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#4CAF50' }}>
          Reactivate Subscription
        </DialogTitle>
        <DialogContent>
          {reactivateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reactivateError}
            </Alert>
          )}

          {/* Option 1: Reactivate current plan */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              mb: 2,
              cursor: reactivateLoading ? 'default' : 'pointer',
              '&:hover': {
                borderColor: reactivateLoading ? 'divider' : 'success.main',
                bgcolor: reactivateLoading ? 'transparent' : 'action.hover',
              },
            }}
            onClick={() => !reactivateLoading && handleReactivateSubscription()}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {reactivateLoading ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              Reactivate current plan
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Resume your {subscription.billing_frequency?.toLowerCase() || ''}{' '}
              {subscription.payment_type || ''} subscription
            </Typography>
          </Box>

          {/* Option 2: Choose a different plan */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              mb: 1,
              cursor: reactivateLoading ? 'default' : 'pointer',
              '&:hover': {
                borderColor: reactivateLoading ? 'divider' : 'primary.main',
                bgcolor: reactivateLoading ? 'transparent' : 'action.hover',
              },
            }}
            onClick={() => {
              if (reactivateLoading) return
              setShowReactivateDialog(false)
              setShowMembershipUpgrade(true)
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Choose a different plan
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Change payment method, billing details or subscription plan
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowReactivateDialog(false)}
            disabled={reactivateLoading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stripe Checkout for Terminated/Switched Subscriptions */}
      <Dialog
        open={showCheckout}
        onClose={() => {
          setShowCheckout(false)
          setCheckoutOptions(null)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Complete Your Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please complete checkout to activate your Stripe subscription.
          </Typography>
          {checkoutOptions && stripePromise ? (
            <Box id="checkout">
              <EmbeddedCheckoutProvider
                key={checkoutOptions.clientSecret}
                stripe={stripePromise}
                options={checkoutOptions}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </Box>
          ) : (
            <Typography>Loading checkout...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowCheckout(false)
              setCheckoutOptions(null)
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
